/**
 * Minimal DOM helpers.
 *
 * `text` is always applied via `textContent`. Player names are supplied by other
 * players and rendered into everyone's page, so nothing user-controlled may ever
 * reach `innerHTML`. `normalizeName` in shared/protocol.ts strips control and
 * invisible characters, but it deliberately does not escape markup — that is
 * this layer's job, and doing it here means it cannot be forgotten per-callsite.
 */

type Attrs = Record<string, string | number | boolean | undefined>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else if (key === 'html') throw new Error('innerHTML is not allowed here');
    else if (value === true) node.setAttribute(key, '');
    else node.setAttribute(key, String(value));
  }

  for (const child of children) {
    node.appendChild(
      typeof child === 'string' ? document.createTextNode(child) : child,
    );
  }

  return node;
}

export function clear(node: Element): void {
  while (node.firstChild !== null) node.removeChild(node.firstChild);
}

export function mount(node: Element, ...children: Node[]): void {
  clear(node);
  for (const child of children) node.appendChild(child);
}

/** Pip layout per face, as unit coordinates in a 3x3 grid. */
const PIPS: Record<number, [number, number][]> = {
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [2, 0], [0, 2], [2, 2]],
  5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]],
  6: [[0, 0], [2, 0], [0, 1], [2, 1], [0, 2], [2, 2]],
};

export type DieState = 'own' | 'hidden' | 'match' | 'wild' | 'plain';

/**
 * One die. `hidden` renders a face-down cup — used for other players, whose
 * faces this client has never been sent in the first place.
 */
export function die(face: number | null, state: DieState = 'own'): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 30 30');
  svg.setAttribute('class', `die die--${state}`);
  svg.setAttribute('role', 'img');
  svg.setAttribute(
    'aria-label',
    face === null ? 'Hidden die' : `Die showing ${face}`,
  );

  const body = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  body.setAttribute('x', '1.5');
  body.setAttribute('y', '1.5');
  body.setAttribute('width', '27');
  body.setAttribute('height', '27');
  body.setAttribute('rx', '6');
  body.setAttribute('class', 'die__body');
  svg.appendChild(body);

  if (face === null) {
    const mark = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    mark.setAttribute('x', '15');
    mark.setAttribute('y', '21');
    mark.setAttribute('text-anchor', 'middle');
    mark.setAttribute('class', 'die__unknown');
    mark.textContent = '?';
    svg.appendChild(mark);
    return svg;
  }

  for (const [column, row] of PIPS[face] ?? []) {
    const pip = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    pip.setAttribute('cx', String(7.5 + column * 7.5));
    pip.setAttribute('cy', String(7.5 + row * 7.5));
    pip.setAttribute('r', '2.6');
    pip.setAttribute('class', 'die__pip');
    svg.appendChild(pip);
  }

  return svg;
}
